import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

export interface TimeseriesSeries {
  key: string;
  label: string;
  color: string;
  values: Array<{ ts: string; value: number }>;
}

interface TimeseriesChartProps {
  series: TimeseriesSeries[];
  height?: number;
  yFormatter?: (v: number) => string;
  xFormatter?: (ts: string) => string;
}

export function TimeseriesChart({
  series,
  height = 240,
  yFormatter,
  xFormatter,
}: TimeseriesChartProps) {
  // Merge all series into a single [{ts, seriesA, seriesB}] shape for recharts.
  const index = new Map<string, Record<string, number | string>>();
  for (const s of series) {
    for (const point of s.values) {
      const row = index.get(point.ts) ?? { ts: point.ts };
      row[s.key] = point.value;
      index.set(point.ts, row);
    }
  }
  const data = Array.from(index.values()).sort((a, b) =>
    String(a.ts).localeCompare(String(b.ts)),
  );
  const fmtX = xFormatter ?? ((ts: string) => ts.slice(5));
  const fmtY = yFormatter ?? ((v: number) => String(v));

  if (data.length === 0) {
    return (
      <div
        role="img"
        aria-label="No data"
        className="flex items-center justify-center rounded-md border border-dashed bg-muted/30 text-xs text-muted-foreground"
        style={{ height }}
      >
        No data in range
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis
            dataKey="ts"
            tickFormatter={fmtX}
            stroke="hsl(var(--muted-foreground))"
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickFormatter={fmtY}
            stroke="hsl(var(--muted-foreground))"
            tick={{ fontSize: 11 }}
            width={48}
          />
          <Tooltip
            contentStyle={{
              background: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              fontSize: 12,
            }}
            labelFormatter={(v) => fmtX(String(v))}
            formatter={(v: number) => [fmtY(v), '']}
          />
          {series.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
